import React, { useState, useEffect } from 'react';
import apiService from '../services/api.service';
import './MenuManagement.css';

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001";

const MenuManagement = () => {
  const [menuPages, setMenuPages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingPage, setEditingPage] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    pageNumber: 1,
    isActive: true,
    image: ''
  });

  // Fetch menu pages
  const fetchMenuPages = async () => {
    setIsLoading(true);
    try {
      const response = await apiService.getMenuPages();
      setMenuPages(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching menu pages:', err);
      setError(`Menü sayfaları yüklenirken bir hata oluştu: ${err.response?.data?.error || err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Load menu pages on component mount
  useEffect(() => {
    fetchMenuPages();
  }, []);

  // Handle file selection
  const handleFileChange = (e) => {
    setSelectedFile(e.target.files[0]);
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  // Upload image and get URL
  const uploadImage = async () => {
    if (!selectedFile) {
      return null;
    }

    const formData = new FormData();
    formData.append('image', selectedFile);

    try {
      const response = await apiService.uploadMenuImage(formData);
      return response.data.imageUrl;
    } catch (err) {
      console.error('Error uploading image:', err);
      setError(`Görsel yüklenirken bir hata oluştu: ${err.response?.data?.error || err.message}`);
      return null;
    }
  };

  // Form submission handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      let imageUrl = formData.image;
      
      // Upload new image if selected
      if (selectedFile) {
        const newImageUrl = await uploadImage();
        if (!newImageUrl) {
          setError('Görsel yüklenemedi. Lütfen tekrar deneyin.');
          return;
        }
        imageUrl = newImageUrl;
      } else if (!editingPage) {
        // Require image for new pages
        setError('Lütfen bir görsel seçin.');
        return;
      }
      
      const pageData = {
        ...formData,
        image: imageUrl
      };
      
      if (editingPage) {
        // Update existing page
        await apiService.updateMenuPage(editingPage.id, pageData);
      } else {
        // Create new page
        await apiService.createMenuPage(pageData);
      }
      
      // Refresh the list and reset form
      await fetchMenuPages();
      resetForm();
      setError(null);
    } catch (err) {
      console.error('Form submission error:', err);
      setError(err.response?.data?.error || 'Bir hata oluştu.');
    }
  };

  // Delete menu page
  const handleDelete = async (id) => {
    if (!window.confirm('Bu menü sayfasını silmek istediğinize emin misiniz?')) {
      return;
    }
    
    try {
      await apiService.deleteMenuPage(id);
      await fetchMenuPages();
      setError(null);
    } catch (err) {
      console.error('Delete error:', err);
      setError(err.response?.data?.error || 'Silme işlemi sırasında bir hata oluştu.');
    }
  };

  // Edit page handler
  const handleEdit = (page) => {
    setEditingPage(page);
    setFormData({
      title: page.title,
      pageNumber: page.pageNumber,
      isActive: page.isActive,
      image: page.image
    });
    setShowForm(true);
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      title: '',
      pageNumber: menuPages.length + 1,
      isActive: true,
      image: ''
    });
    setSelectedFile(null);
    setEditingPage(null);
    setShowForm(false);
  };

  // Reorder pages (up/down)
  const handleReorder = async (id, direction) => {
    const pageIndex = menuPages.findIndex(page => page.id === id);
    if (pageIndex === -1) return;
    
    // Check boundaries
    if (
      (direction === 'up' && pageIndex === 0) || 
      (direction === 'down' && pageIndex === menuPages.length - 1)
    ) {
      return;
    }
    
    // Find target page
    const targetIndex = direction === 'up' ? pageIndex - 1 : pageIndex + 1;
    const currentPage = menuPages[pageIndex];
    const targetPage = menuPages[targetIndex];
    
    try {
      // Swap page numbers
      await Promise.all([
        apiService.updateMenuPage(currentPage.id, { pageNumber: targetPage.pageNumber }),
        apiService.updateMenuPage(targetPage.id, { pageNumber: currentPage.pageNumber })
      ]);
      
      // Refresh list
      await fetchMenuPages();
    } catch (err) {
      console.error('Reorder error:', err);
      setError(`Sıralama değiştirme sırasında bir hata oluştu: ${err.response?.data?.error || err.message}`);
    }
  };

  // Toggle active status
  const toggleActive = async (page) => {
    try {
      await apiService.updateMenuPage(page.id, { isActive: !page.isActive });
      await fetchMenuPages();
    } catch (err) {
      console.error('Toggle active error:', err);
      setError(`Durum değiştirme sırasında bir hata oluştu: ${err.response?.data?.error || err.message}`);
    }
  };

  return (
    <div className="menu-management">
      <div className="management-header">
        <h2>Menü Yönetimi</h2>
        <button 
          className="add-button"
          onClick={() => {
            if (showForm && !editingPage) {
              resetForm();
            } else {
              setShowForm(true);
              setEditingPage(null);
              setFormData({
                title: '',
                pageNumber: menuPages.length + 1,
                isActive: true,
                image: ''
              });
            }
          }}
        >
          {showForm && !editingPage ? 'İptal' : 'Yeni Sayfa Ekle'}
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {showForm && (
        <div className="menu-form">
          <h3>{editingPage ? 'Menü Sayfasını Düzenle' : 'Yeni Menü Sayfası Ekle'}</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="title">Başlık:</label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="Sayfa başlığı"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="pageNumber">Sayfa Sırası:</label>
              <input
                type="number"
                id="pageNumber"
                name="pageNumber"
                value={formData.pageNumber}
                onChange={handleInputChange}
                min="1"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="isActive" className="checkbox-label">
                <input
                  type="checkbox"
                  id="isActive"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleInputChange}
                />
                Aktif
              </label>
            </div>

            <div className="form-group">
              <label htmlFor="image">Menü Görseli:</label>
              <input
                type="file"
                id="image"
                name="image"
                accept="image/*"
                onChange={handleFileChange}
                className="file-input"
              />
              {editingPage && formData.image && (
                <div className="current-image">
                  <p>Mevcut görsel:</p>
                  <img 
                    src={formData.image.startsWith('/') ? `${API_URL}${formData.image}` : formData.image} 
                    alt="Mevcut menü görseli" 
                    width="100" 
                  />
                </div>
              )}
            </div>

            <div className="form-actions">
              <button type="submit" className="submit-button">
                {editingPage ? 'Güncelle' : 'Ekle'}
              </button>
              <button type="button" className="cancel-button" onClick={resetForm}>
                İptal
              </button>
            </div>
          </form>
        </div>
      )}

      {isLoading ? (
        <div className="loading">Yükleniyor...</div>
      ) : menuPages.length === 0 ? (
        <div className="empty-state">
          <p>Henüz hiç menü sayfası eklenmemiş.</p>
        </div>
      ) : (
        <div className="menu-pages-list">
          <table>
            <thead>
              <tr>
                <th>Sıra</th>
                <th>Başlık</th>
                <th>Görsel</th>
                <th>Durum</th>
                <th>İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {menuPages.map((page) => (
                <tr key={page.id} className={page.isActive ? '' : 'inactive-row'}>
                  <td>{page.pageNumber}</td>
                  <td>{page.title}</td>
                  <td>
                    <img 
                      src={page.image.startsWith('/') ? `${API_URL}${page.image}` : page.image} 
                      alt={page.title} 
                      className="thumbnail" 
                    />
                  </td>
                  <td>
                    <span className={`status-badge ${page.isActive ? 'active' : 'inactive'}`}>
                      {page.isActive ? 'Aktif' : 'Pasif'}
                    </span>
                  </td>
                  <td>
                    <div className="table-actions">
                      <button 
                        onClick={() => handleReorder(page.id, 'up')} 
                        className="action-button" 
                        title="Yukarı Taşı"
                        disabled={menuPages.indexOf(page) === 0}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M12 19V5M5 12l7-7 7 7"/>
                        </svg>
                      </button>
                      <button 
                        onClick={() => handleReorder(page.id, 'down')} 
                        className="action-button" 
                        title="Aşağı Taşı"
                        disabled={menuPages.indexOf(page) === menuPages.length - 1}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M12 5v14M5 12l7 7 7-7"/>
                        </svg>
                      </button>
                      <button 
                        onClick={() => toggleActive(page)} 
                        className="action-button" 
                        title={page.isActive ? 'Pasif Yap' : 'Aktif Yap'}
                      >
                        {page.isActive ? (
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M18.6 6.6L6 19.2"></path>
                            <path d="M6 6l12.6 12.6"></path>
                          </svg>
                        ) : (
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M20 6L9 17l-5-5"></path>
                          </svg>
                        )}
                      </button>
                      <button 
                        onClick={() => handleEdit(page)} 
                        className="action-button" 
                        title="Düzenle"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                        </svg>
                      </button>
                      <button 
                        onClick={() => handleDelete(page.id)} 
                        className="action-button" 
                        title="Sil"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                          <line x1="10" y1="11" x2="10" y2="17"></line>
                          <line x1="14" y1="11" x2="14" y2="17"></line>
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default MenuManagement;